/**
 * Sentry Error Tracking
 * 
 * Keskitetty Sentry-alustus PWA-sovellukselle.
 * - Offline transport: virheet säilyvät vaikka verkko katkeaa
 * - GDPR-turvallinen Session Replay: kaikki teksti ja syötteet maskataan
 * - beforeSend-suodatus: poistetaan tunnetut ei-kriittiset virheet
 */
import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry - call before anything else in main.tsx
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  // Don't initialize in development or if DSN is missing
  if (import.meta.env.DEV || !dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Performance
    tracesSampleRate: 0.1,

    // Session Replay - GDPR-turvallinen
    replaysSessionSampleRate: 0,    // Ei tavallisia sessio-otoksia
    replaysOnErrorSampleRate: 1.0,  // 100% virheissä

    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
      Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    ],

    // Suodata tunnetut ei-kriittiset virheet
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || "";

      // ResizeObserver loop error - harmless browser bug
      if (message.includes("ResizeObserver loop")) {
        return null;
      }

      // Offline fetch errors - expected in PWA
      if (message.includes("Failed to fetch") || message.includes("NetworkError")) {
        return null;
      }

      // JWT expired - handled by auth flow
      if (message.includes("JWT expired") || message.includes("invalid JWT")) {
        return null;
      }

      return event;
    },

    // Poista console.log-tason leipämurut
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console" && breadcrumb.level === "log") {
        return null;
      }
      return breadcrumb;
    },
  });
}

/**
 * Set authenticated user context for Sentry
 * Only user ID and role - no PII
 */
export function setSentryUser(userId: string, role: string): void {
  Sentry.setUser({ id: userId, role });
}

/**
 * Clear user context on sign out
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Set additional context for debugging
 */
export function setSentryContext(name: string, data: Record<string, unknown>): void {
  Sentry.setContext(name, data);
}

// Re-export Sentry for direct usage where needed
export { Sentry };
